import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './ApiOfflineBanner.css';

const HEALTH_RETRIES = 3;
const RETRY_DELAY_MS = 1500;
const RECHECK_INTERVAL_MS = 25000;

/**
 * Faz health check na API com algumas tentativas. Só exibe o banner
 * se falhar todas. Quando exibido, recheca de tempos em tempos e esconde se voltar.
 */
const ApiOfflineBanner = () => {
  const [show, setShow] = useState(false);
  const [checked, setChecked] = useState(false);

  const checkHealth = useCallback(() => {
    return api.get('health').then(() => true).catch(() => false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const tryWithRetries = (attempt = 0) => {
      if (cancelled) return;
      checkHealth()
        .then((ok) => {
          if (cancelled) return;
          if (ok) {
            setShow(false);
            setChecked(true);
            return;
          }
          if (attempt < HEALTH_RETRIES - 1) {
            setTimeout(() => tryWithRetries(attempt + 1), RETRY_DELAY_MS);
          } else {
            setShow(true);
            setChecked(true);
          }
        });
    };

    tryWithRetries();
    return () => { cancelled = true; };
  }, [checkHealth]);

  useEffect(() => {
    if (!show) return;
    const interval = setInterval(() => {
      checkHealth().then((ok) => {
        if (ok) setShow(false);
      });
    }, RECHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [show, checkHealth]);

  const handleRetry = () => {
    let attempt = 0;
    const tryAgain = () => {
      checkHealth().then((ok) => {
        if (ok) setShow(false);
        else if (attempt < HEALTH_RETRIES - 1) {
          attempt += 1;
          setTimeout(tryAgain, RETRY_DELAY_MS);
        }
      });
    };
    tryAgain();
  };

  if (!show || !checked) return null;

  return (
    <>
      <div className="api-offline-banner" role="alert">
        <span className="api-offline-text">
          O servidor parece estar indisponível (erro 502 ou de conexão). Login e outros dados podem falhar.
          Verifique se o backend está rodando e se a URL da API está correta. Veja TROUBLESHOOTING_502.md.
        </span>
        <button type="button" className="api-offline-retry" onClick={handleRetry}>
          Tentar novamente
        </button>
        <button type="button" className="api-offline-dismiss" onClick={() => setShow(false)} aria-label="Fechar">
          ✕
        </button>
      </div>
      <div className="api-offline-spacer" aria-hidden="true" />
    </>
  );
};

export default ApiOfflineBanner;
